import { faHeart as faRegularHeart } from "@fortawesome/free-regular-svg-icons";
import {
  faCircleInfo,
  faExclamationTriangle,
  faHeart as faSolidHeart,
  faRss,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Panel } from "primereact/panel";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useSearchParams } from "react-router-dom";

import { getDepartures } from "../services/sl";
import { useLocale } from "../stores/locale";
import { useSavedStationTransports } from "../stores/saved-station-transports";
import { Departure, StopDeviation } from "../types/departure";
import { TransportType } from "../types/line";
import { Station } from "../types/station";
import { getLineColor } from "./line-icon";
import { PrimeButton } from "./prime/button";
import { TransportIcon } from "./transport-icon";

export function Station({
  station,
  className,
}: {
  station: Station;
  className?: string;
}) {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    savedStationTransports,
    addSavedStationTransport,
    removeSavedStationTransport,
  } = useSavedStationTransports();
  const t = useLocale((state) => state.locale);

  const [isLoading, setIsLoading] = useState(false);
  const [departures, setDepartures] = useState<Departure[] | null>(null);
  const [stopDeviations, setStopDeviations] = useState<StopDeviation[] | null>(
    null,
  );
  const [highestSeverity, setHighestSeverity] = useState<number>(0);
  const [transportTypes, setTransportTypes] = useState<TransportType[] | null>(
    null,
  );
  const [filterByLine, setFilterByLine] = useState<{
    lineId: number;
    direction: string;
  } | null>(null);
  const [
    isCurrentUrlSavedStationTransport,
    setIsCurrentUrlSavedStationTransport,
  ] = useState(false);

  const currentTransportType = searchParams.get("type") as TransportType;

  const updateDepartures = useCallback(async () => {
    const { departures, stopDeviations } = await getDepartures({
      station: station.id,
    });
    setDepartures(departures);
    setStopDeviations(stopDeviations);
    setHighestSeverity(
      stopDeviations.reduce((acc, stopDeviation) => {
        return Math.max(acc, stopDeviation.importance_level);
      }, 0),
    );
    const transportType = departures.reduce(
      (acc, departure) => [
        ...acc,
        ...(departure.line.transport_mode &&
        !acc.some((d) => d === departure.line.transport_mode)
          ? [departure.line.transport_mode]
          : []),
      ],
      [],
    );
    setTransportTypes(transportType);
  }, [station]);

  useEffect(() => {
    let isMounted = true;

    let interval: NodeJS.Timeout | undefined;
    (async () => {
      setIsLoading(true);
      await updateDepartures();
      setIsLoading(false);
      if (!isMounted) {
        return;
      }
      interval = setInterval(() => {
        updateDepartures();
      }, 30 * 1000);
    })();

    return () => {
      isMounted = false;
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [station, updateDepartures]);

  useEffect(() => {
    if (!departures) return;
    if (
      !currentTransportType ||
      !transportTypes?.includes(currentTransportType)
    ) {
      const defaultTransportType = transportTypes?.[0];
      if (!defaultTransportType) {
        return;
      }
      setSearchParams({
        ...Object.fromEntries(searchParams.entries()),
        type: defaultTransportType,
      });
    }
  }, [
    currentTransportType,
    transportTypes,
    location.pathname,
    departures,
    setSearchParams,
    searchParams,
  ]);

  useEffect(() => {
    setIsCurrentUrlSavedStationTransport(
      savedStationTransports.some(
        (savedStationTransport) =>
          savedStationTransport.stationId === station.id &&
          savedStationTransport.transportType === currentTransportType,
      ),
    );
  }, [savedStationTransports, station.id, currentTransportType]);

  useEffect(() => {
    if (filterByLine) {
      setFilterByLine(null);
    }
  }, [currentTransportType]);

  return (
    <div>
      {stopDeviations?.length && stopDeviations.length > 0 ? (
        <StopDeviationList
          highestSeverity={highestSeverity}
          stopDeviations={stopDeviations}
        />
      ) : null}
      <div className={`bg-white ${className}`}>
        <div className="flex min-h-12 border-b border-gray-300">
          {transportTypes?.map((transportType) => (
            <NavLink
              key={transportType}
              className={`flex items-center justify-center px-4 ${transportType === currentTransportType ? "font-bold text-blue-500 border-b-2 border-blue-500" : ""}`}
              to={`?station=${station.id}&type=${transportType}`}
            >
              <TransportIcon transportType={transportType} />
            </NavLink>
          ))}
          <div className="grow flex items-center justify-end">
            <PrimeButton
              icon={
                <FontAwesomeIcon
                  color="red"
                  icon={
                    isCurrentUrlSavedStationTransport
                      ? faSolidHeart
                      : faRegularHeart
                  }
                />
              }
              text={true}
              onClick={() => {
                if (isCurrentUrlSavedStationTransport) {
                  removeSavedStationTransport({
                    stationId: station.id,
                    transportType: currentTransportType,
                  });
                } else {
                  addSavedStationTransport({
                    stationId: station.id,
                    transportType: currentTransportType,
                  });
                }
              }}
            />
          </div>
        </div>
        <ul className="flex flex-col gap-4 p-2">
          {!isLoading &&
            stopDeviations &&
            departures
              ?.filter(
                (departure) =>
                  departure.line.transport_mode === currentTransportType,
              )
              .filter(
                (departure) =>
                  filterByLine === null ||
                  (filterByLine.lineId === departure.line.id &&
                    filterByLine.direction === departure.direction),
              )
              .map((departure, index) => (
                <li key={index} className="flex items-center gap-3">
                  <PrimeButton
                    className={`cursor-pointer relative rounded-lg ${getLineColor(departure.line)} text-center text-white min-w-[50px] px-2`}
                    unstyled={true}
                    onClick={() =>
                      setFilterByLine(
                        filterByLine?.lineId === departure.line.id &&
                          filterByLine?.direction === departure.direction
                          ? null
                          : {
                              lineId: departure.line.id,
                              direction: departure.direction,
                            },
                      )
                    }
                  >
                    {departure.line.designation}
                    {departure.scheduled && (
                      <FontAwesomeIcon
                        className="absolute top-[-5px] right-[-5px] text-xs text-green-500"
                        icon={faRss}
                      />
                    )}
                  </PrimeButton>
                  <div className="grow flex items-center gap-2">
                    {departure.stop_point.designation && (
                      <span className="rounded-sm text-sm text-center bg-gray-200 text-gray-500 px-2">
                        {departure.stop_point.designation}
                      </span>
                    )}
                    {departure.direction}
                  </div>
                  <div className="whitespace-nowrap overflow-hidden text-ellipsis">
                    {!isDisplayTimeClock(departure.display) ? (
                      <>
                        {departure.display} ({departureTimeDisplay(departure)})
                      </>
                    ) : (
                      <>{departureTimeDisplay(departure)}</>
                    )}
                  </div>
                </li>
              ))}
        </ul>
      </div>
    </div>
  );
}

function departureTimeDisplay(departure: Departure) {
  return (
    <>
      {formatTime(departure.expected || departure.scheduled)}
      {departure.expected &&
        Math.abs(
          departure.expected.getTime() - departure.scheduled.getTime(),
        ) >=
          1000 * 60 && (
          <span
            className="text-red-500 line-through
"
          >
            &nbsp;
            {formatTime(departure.scheduled)}
          </span>
        )}
    </>
  );
}

function StopDeviationList({
  stopDeviations,
  highestSeverity,
}: {
  stopDeviations: StopDeviation[];
  highestSeverity: number;
}) {
  const t = useLocale((state) => state.locale);
  const ref = useRef<Panel>(null);
  return (
    <Panel
      ref={ref}
      toggleable
      className="mb-4"
      collapsed={highestSeverity <= 6}
      header={
        <div
          className="flex items-center gap-2 w-full border-1 border-gray-300"
          role="button"
          tabIndex={0}
        >
          <FontAwesomeIcon
            color={getSeverityColor(highestSeverity)}
            icon={getSeverityIcon(highestSeverity)}
          />
          {t.departures.stopDeviations}
        </div>
      }
      onClick={(e) => ref.current?.toggle(e)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          ref.current?.toggle(e);
        }
      }}
    >
      <div className="flex flex-col gap-2">
        {stopDeviations.map((stopDeviation, index) => (
          <div
            key={`stop-deviation-${index}`}
            className="flex items-center gap-2"
            role="button"
            tabIndex={0}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
              }
            }}
          >
            {stopDeviation.scope.lines?.map((line) => (
              <div key={`stop-deviation-${index}-${line.id}`}>
                <div
                  className={`relative rounded-lg ${getLineColor(line)} text-center text-white min-w-[50px] px-2 flex gap-2 items-center`}
                >
                  <TransportIcon
                    size="xs"
                    transportType={line.transport_mode as TransportType}
                  />
                  {line.designation}
                </div>
              </div>
            ))}
            {stopDeviation.message}
          </div>
        ))}
      </div>
    </Panel>
  );
}

function getSeverityIcon(severity: number) {
  if (severity >= 1 && severity <= 3) {
    return faCircleInfo;
  }
  if (severity >= 4 && severity <= 6) {
    return faExclamationTriangle;
  }
  return faExclamationTriangle;
}

function getSeverityColor(severity: number) {
  if (severity >= 1 && severity <= 3) {
    return "blue";
  }
  if (severity >= 4 && severity <= 6) {
    return "orange";
  }
  return "red";
}
function formatTime(time: Date) {
  return time.toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isDisplayTimeClock(displayTime: string) {
  return displayTime.includes(":");
}
